/**
 * Public legal document screen (privacy, data deletion, terms).
 * Rendered from `app/[slug].tsx` for /privacy, /data-deletion, and /terms.
 */

import React, { useEffect, useMemo } from 'react'
import { View, Text, ScrollView, Pressable, Platform } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { buildPublicSignInHref } from '../../../src/signIn/helpers/postAuthRedirect.helper'
import { MarketingPageFooter } from '../MarketingPageFooter/MarketingPageFooter'
import Head from 'expo-router/head'
import {
  getLegalDocumentPageContent,
  shouldShowDocumentVersion,
} from '../../../src/landing/helpers/legalDocumentContent.helper'
import {
  LEGAL_DOCUMENT_VERSION,
  LEGAL_DOCUMENT_LAST_UPDATED,
} from '../../../src/landing/constants/legalDocumentVersion.constants'
import { SITE_URL } from '../../../src/shared/constants/seo.constants'
import type { LegalDocumentVariant } from '../../../src/landing/types/legalDocument.types'
import { usePublicMarketingWebScrollLayout } from '../../../src/shared/hooks/usePublicMarketingWebScrollLayout'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { LanguageContext } from '../../../src/i18n'
import { legalDocumentScreenStyles as styles } from './LegalDocumentScreen.styles'

export type { LegalDocumentVariant }

export interface LegalDocumentScreenProps {
  variant: LegalDocumentVariant
  /** When app, back navigates to in-app Privacy and data hub instead of marketing home. */
  origin?: 'marketing' | 'app'
  /** Root path segment, used for the canonical URL on web. Omitted inside the app shell. */
  slug?: string
}

export const LegalDocumentScreen: React.FC<LegalDocumentScreenProps> = ({
  variant,
  origin = 'marketing',
  slug,
}) => {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const languageContext = React.useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LegalDocumentScreen must be used within LanguageProvider')
  }
  const { t, language } = languageContext
  usePublicMarketingWebScrollLayout(origin === 'marketing')

  const { title, sections } = useMemo(
    () => getLegalDocumentPageContent(variant, t, language),
    [variant, t, language]
  )
  const shouldShowPrivacyControlsCallout =
    variant !== 'credits' && variant !== 'about' && variant !== 'support'
  const showsVersion = shouldShowDocumentVersion(variant)
  const versionLine = t.legal.documentVersionLine
    .replace('{date}', LEGAL_DOCUMENT_LAST_UPDATED)
    .replace('{version}', LEGAL_DOCUMENT_VERSION)
  const canonicalUrl = slug ? `${SITE_URL}/${slug}` : SITE_URL
  // "About Zporti" already names the brand; don't render "About Zporti · Zporti".
  const pageTitle = title.includes('Zporti') ? title : `${title} · Zporti`
  const metaDescription = sections[0]?.paragraphs[0] ?? ''

  const handleOpenPrivacyControls = () => {
    if (isSignedIn) {
      router.push('/privacy-controls')
      return
    }
    router.push(buildPublicSignInHref({ returnUrl: '/privacy-controls' }) as Href)
  }

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    document.title = `${title} · Zporti`
    return () => {
      document.title = 'Zporti'
    }
  }, [title])

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#1F2937'
    return () => {
      document.body.style.backgroundColor = prev
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return
    // Always open legal documents from the top on route/language changes.
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [variant, language])

  const documentBody = (
    <>
      <Pressable
        accessibilityRole="link"
        onPress={() => {
          if (origin === 'app') {
            if (router.canGoBack()) {
              router.back()
            } else {
              router.replace('/app/')
            }
            return
          }
          router.push('/')
        }}
        style={({ pressed, hovered }) => [
          styles.backLink,
          pressed && styles.backLinkPressed,
          hovered && Platform.OS === 'web' && styles.backLinkHovered,
        ]}
      >
        <Text style={styles.backLinkText}>
          {origin === 'app'
            ? t.legal.backToPrivacyAndData
            : t.legal.backToHome}
        </Text>
      </Pressable>

      <Text
        style={[styles.title, !showsVersion && styles.titleOnly]}
        accessibilityRole="header"
      >
        {title}
      </Text>

      {showsVersion ? (
        <Text style={styles.versionLine}>{versionLine}</Text>
      ) : null}

      {origin === 'marketing' && isLoaded && shouldShowPrivacyControlsCallout ? (
        <View style={styles.privacyControlsCallout}>
          <Text style={styles.privacyControlsCalloutText}>
            {t.legal.privacyControlsCallout}
          </Text>
          <Pressable
            accessibilityRole="link"
            onPress={handleOpenPrivacyControls}
            style={({ pressed, hovered }) => [
              styles.privacyControlsLink,
              pressed && styles.privacyControlsLinkPressed,
              hovered && Platform.OS === 'web' && styles.privacyControlsLinkHovered,
            ]}
          >
            <Text style={styles.privacyControlsLinkText}>
              {isSignedIn ? t.legal.openPrivacyControls : t.legal.signInForPrivacyControls}
            </Text>
          </Pressable>
        </View>
      ) : null}

      {sections.map((section) => (
        <View key={section.title} style={styles.section}>
          <Text style={styles.sectionTitle}>{section.title}</Text>
          {section.paragraphs.map((paragraph, index) => (
            <Text
              key={`${section.title}-${index}`}
              style={styles.paragraph}
            >
              {paragraph}
            </Text>
          ))}
        </View>
      ))}

    </>
  )

  const marketingFooter =
    origin === 'marketing' ? <MarketingPageFooter contactMode="route" /> : null

  if (Platform.OS === 'web' && origin === 'marketing') {
    return (
      <>
        {/*
          Emitted into the exported HTML head, so each document has its own title,
          description, and canonical URL rather than sharing the site-wide defaults.
          The document.title effect above remains as the client-side fallback.
        */}
        <Head>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:url" content={canonicalUrl} />
        </Head>
        <View style={styles.webPageRoot}>
          <View style={styles.webDocumentRoot}>{documentBody}</View>
          {marketingFooter}
        </View>
      </>
    )
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={true}
    >
      {documentBody}
      {marketingFooter}
    </ScrollView>
  )
}
